//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { DimensionFormat } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { ProductDetailsFieldLabels } from '@campaigns/components/shared/product-details-modal/drawer/constants/ProductDetailsModalFieldLabels';
import { DIMENSION_FORMATS } from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { SAMPLING_CAMPAIGN_PRODUCT_DRAWER_PREFIX } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { validateDimension } from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import {
  BodySecondaryText,
  BodyText,
  CaptionErrorText,
} from '@components/atoms/text/Text';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { FormTextInput } from '@components/form/text-input/FormTextInput';
import { RadioGroup } from '@components/primitives/radio-group/RadioGroup';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import { isDefined, isEmptyValue } from '@utils/filterPredicates';

const DEFAULT_DIMENSION_FORMAT: DimensionFormat = 'IN';

const DIMENSION_FIELDS = [
  { key: 'depth', label: 'Depth' },
  { key: 'width', label: 'Width' },
  { key: 'height', label: 'Height' },
] as const;

interface ProductSizeFieldsProps {
  isViewOnly: boolean;
}

// Only collected for BV Shipped campaigns, where we pack and ship the products.
// Optional here: missing dimensions are still requested in the fulfillment section.
export const ProductSizeFields = ({ isViewOnly }: ProductSizeFieldsProps) => {
  const { samplingCampaignRequest } = useSamplingCampaign();
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<EditableBVSamplingProduct>();
  const format = useWatch({ control, name: 'dimensions.format' });

  const fulfillmentOptions = samplingCampaignRequest?.fulfillmentOptions;
  const isBVShipped =
    fulfillmentOptions?.fulfillmentType === 'shipped' &&
    fulfillmentOptions.whoShipsProduct === 'bazaarvoice';

  useEffect(() => {
    if (isBVShipped && !isViewOnly && !isDefined(format)) {
      setValue('dimensions.format', DEFAULT_DIMENSION_FORMAT);
    }
  }, [format, isBVShipped, isViewOnly, setValue]);

  if (!isBVShipped) {
    return null;
  }

  if (isViewOnly) {
    const dimensions = getValues('dimensions');
    const values = DIMENSION_FIELDS.map(({ key }) => dimensions?.[key]);
    return (
      <Container>
        <BodyText>{ProductDetailsFieldLabels.dimensions}</BodyText>
        <BodyText>
          {values.some(isEmptyValue)
            ? DEFAULT_EMPTY_VALUE
            : `${values.join(' x ')} ${dimensions?.format ?? ''}`.trim()}
        </BodyText>
      </Container>
    );
  }

  const dimensionErrors = DIMENSION_FIELDS.map(
    ({ key }) => errors.dimensions?.[key]?.message,
  ).filter(isDefined);

  const handleFormatChange = (nextFormat: DimensionFormat) => {
    setValue('dimensions.format', nextFormat, { shouldDirty: true });
  };

  return (
    <Container>
      <BodyText>{ProductDetailsFieldLabels.dimensions}</BodyText>
      <StyledRadioGroup
        aria-label={`${ProductDetailsFieldLabels.dimensions} units`}
        onValueChange={handleFormatChange}
        value={format ?? DEFAULT_DIMENSION_FORMAT}
      >
        {Object.entries(DIMENSION_FORMATS).map(([value, { label }]) => {
          const id = `product-drawer-dimensions-format-${value}`;
          return (
            <RadioOption key={value}>
              <RadioGroup.Item
                id={id}
                trackingId={`${SAMPLING_CAMPAIGN_PRODUCT_DRAWER_PREFIX}dimensions.format-${value}`}
                value={value}
              >
                <RadioGroup.Indicator />
              </RadioGroup.Item>
              <RadioGroup.Label htmlFor={id}>
                <BodyText>{label}</BodyText>
              </RadioGroup.Label>
            </RadioOption>
          );
        })}
      </StyledRadioGroup>
      <DimensionInputs>
        {DIMENSION_FIELDS.map(({ key, label }) => {
          const id = `product-drawer-dimensions-${key}`;
          return (
            <DimensionInput key={key}>
              <label htmlFor={id}>
                <BodySecondaryText>{label}</BodySecondaryText>
              </label>
              <FormFieldController
                control={control}
                name={`dimensions.${key}`}
                rules={{
                  validate: (value) => validateDimension(value, label),
                }}
              >
                {(props) => (
                  <NumberInput
                    {...props}
                    // Errors are grouped below the row to keep the inputs aligned
                    error={undefined}
                    formComponentId={id}
                    placeholder='10'
                    trackingId={`${SAMPLING_CAMPAIGN_PRODUCT_DRAWER_PREFIX}dimensions.${key}`}
                  />
                )}
              </FormFieldController>
            </DimensionInput>
          );
        })}
      </DimensionInputs>
      {dimensionErrors.map((message) => (
        <CaptionErrorText key={message}>{message}</CaptionErrorText>
      ))}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const DimensionInputs = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const DimensionInput = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const NumberInput = styled(FormTextInput)`
  width: calc(${({ theme }) => theme.spacings[3]} * 3.5);
`;
